/**
 * VH-83: Drag-and-drop or click-to-pick uploader for cookbook OCR.
 * Validates file type and size client-side before invoking the analyzer.
 */

import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CameraIcon, PhotoIcon } from '@heroicons/react/24/outline'

const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const MAX_BYTES = 10 * 1024 * 1024

export default function CapturePhotoUploader({ onAnalyze, analyzing, error }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [localError, setLocalError] = useState(null)
  const [dragOver, setDragOver] = useState(false)

  const handleFile = (f) => {
    if (!f) return
    if (!ACCEPTED.includes(f.type)) {
      setLocalError(t('recipes.photo.wrongFormat'))
      setFile(null)
      setPreview(null)
      return
    }
    if (f.size > MAX_BYTES) {
      setLocalError(t('recipes.photo.fileTooLarge'))
      setFile(null)
      setPreview(null)
      return
    }
    setLocalError(null)
    setFile(f)
    const reader = new FileReader()
    reader.onload = () => setPreview(reader.result)
    reader.readAsDataURL(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer?.files?.[0])
  }

  const handleAnalyze = () => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    onAnalyze?.(fd)
  }

  return (
    <div className="space-y-4">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
        }`}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="photo-file-input"
        />
        {preview ? (
          <img
            src={preview}
            alt="cookbook preview"
            className="mx-auto max-h-64 rounded"
          />
        ) : (
          <>
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-700">
              {t('recipes.photo.dropZone')}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {t('recipes.photo.supported')}
            </p>
          </>
        )}
      </div>

      {(localError || error) && (
        <p className="text-sm text-red-600" data-testid="photo-error">
          {localError || error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!file || analyzing}
          onClick={handleAnalyze}
          className="btn-primary flex items-center gap-2"
          data-testid="photo-analyze-submit"
        >
          <CameraIcon className="h-4 w-4" />
          {analyzing
            ? t('recipes.photo.analyzing')
            : t('recipes.photo.analyze')}
        </button>
      </div>
    </div>
  )
}
